// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { AdvisoryBankError } from '@shared/data-access/types/brokerage-types';
import { type TranslationKey } from '@shared/ui-kit';

interface ErrorMapping {
  regex: RegExp;
  action: AdvisoryBankError;
  i18nKey?: TranslationKey<'errors'>;
}

export const ERROR_CODES: ErrorMapping[] = [
  // New error keywords
  { regex: /^2fa/, action: AdvisoryBankError.Reconnect },
  { regex: /^expired_token/, action: AdvisoryBankError.Reconnect },
  { regex: /^temporary_unavailable/, action: AdvisoryBankError.Retry },
  { regex: /^processing_error/, action: AdvisoryBankError.Retry },
  { regex: /^generic_error/, action: AdvisoryBankError.Retry },

  // Legacy error codes
  { regex: /^ITEM_LOGIN_REQUIRED$/, action: AdvisoryBankError.Reconnect },
  { regex: /El token \w+ ha sido revocado/, action: AdvisoryBankError.Reconnect },
  {
    regex: /Entidad no accesible en este horario/,
    action: AdvisoryBankError.Ignore,
  },
  {
    regex: /Existe una sesión activa para este usuario/,
    action: AdvisoryBankError.Retry,
  },
  { regex: /Error retrieving aggregator info/, action: AdvisoryBankError.Retry },
  {
    regex: /Este token ha sido invalidado/,
    action: AdvisoryBankError.Reconnect,
  },
  {
    regex: /We encountered an unexpected issue while processing your holdings/,
    action: AdvisoryBankError.Retry,
  },
  {
    regex: /^POLLING_TIMED_OUT$/,
    action: AdvisoryBankError.Retry,
    i18nKey: 'errors.polling-timed-out',
  },
];

export const getAction = (errorMessage?: string | null): AdvisoryBankError => {
  if (!errorMessage) return AdvisoryBankError.Ignore;

  const errorCode = ERROR_CODES.find(
    err => errorMessage && err.regex.test(errorMessage),
  );

  return errorCode ? errorCode.action : AdvisoryBankError.Retry;
};

export const getTranslationKey = (
  errorMessage?: string | null,
): TranslationKey<'errors'> | undefined => {
  const errorCode = ERROR_CODES.find(
    err => errorMessage && err.regex.test(errorMessage),
  );

  return errorCode ? errorCode.i18nKey : undefined;
};

export const cleanErrorMessage = (errorMessage?: string | null) => {
  if (!errorMessage) return errorMessage;

  const errorCodePrefixes = [
    '2fa:',
    'expired_token:',
    'temporary_unavailable:',
    'processing_error:',
    'generic_error:',
  ];

  for (const prefix of errorCodePrefixes) {
    if (errorMessage.startsWith(prefix)) {
      return errorMessage.substring(prefix.length).trim();
    }
  }

  return errorMessage;
};
